/**
 * useLoanBooks — Central hook for resolved loan books.
 *
 * Calls getLoanBooks callable which handles both legacy (enabledTemplateIds)
 * and unified (loan_books subcollection) modes server-side.
 *
 * Returns ResolvedLoanBook[] that pages use for loan book selectors/tabs.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';

interface UseLoanBooksParams {
  companyId: string | undefined;
}

interface UseLoanBooksReturn {
  loanBooks: ResolvedLoanBook[];
  enabledLoanBooks: ResolvedLoanBook[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLoanBooks({ companyId }: UseLoanBooksParams): UseLoanBooksReturn {
  const [loanBooks, setLoanBooks] = useState<ResolvedLoanBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!companyId) {
      setLoanBooks([]);
      return;
    }

    let cancelled = false;
    const fetchLoanBooks = async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<{ companyId: string }, ResolvedLoanBook[]>(
          functions,
          'getLoanBooks'
        );
        const result = await fn({ companyId });
        if (!cancelled) {
          setLoanBooks(result.data);
        }
      } catch (err: any) {
        console.error('[useLoanBooks] Failed to fetch loan books:', err);
        if (!cancelled) {
          setError(err.message || 'Failed to load loan books');
          setLoanBooks([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchLoanBooks();

    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey]);

  const enabledLoanBooks = loanBooks.filter((lb) => lb.isEnabled);

  return { loanBooks, enabledLoanBooks, loading, error, refresh };
}
